import React, { useState, useRef } from 'react';
import { apiClient, ApiClientError } from '../services/api-client';
import { useSimpleLoading } from '../hooks/useLoading';
import './FileUpload.css';

interface FileUploadProps {}

const FileUpload: React.FC<FileUploadProps> = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<{
    success: boolean;
    message: string;
    details?: any;
  } | null>(null);
  
  const { loading: uploading, withLoading } = useSimpleLoading();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileValidationAndSet(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      // Directly handle the file without creating a fake event
      handleFileValidationAndSet(file);
    }
  };

  const handleFileValidationAndSet = (file: File) => {
    // Validate file type - now accepts both Excel and image files
    const validExcelTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    const isExcelFile = validExcelTypes.includes(file.type) || 
                       file.name.endsWith('.xlsx') || 
                       file.name.endsWith('.xls');
    
    const isImageFile = file.type.startsWith('image/');
    
    if (!isExcelFile && !isImageFile) {
      setUploadResult({
        success: false,
        message: 'Excelファイル（.xlsx, .xls）または画像ファイル（.jpg, .png, .gif等）を選択してください'
      });
      return;
    }
    
    // Validate file size (max 10MB for Excel, 5MB for images)
    const maxSize = isImageFile ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
    const maxSizeText = isImageFile ? '5MB' : '10MB';
    
    if (file.size > maxSize) {
      setUploadResult({
        success: false,
        message: `ファイルサイズは${maxSizeText}以下にしてください`
      });
      return;
    }
    
    setSelectedFile(file);
    setUploadResult(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploadResult(null);

    try {
      const isImageFile = selectedFile.type.startsWith('image/');
      
      if (isImageFile) {
        // Image file processing
        const result = await withLoading(() => 
          apiClient.uploadExcelFile(selectedFile) // This will handle image files too
        );

        const imageInfo = (result as any).imageInfo;
        
        setUploadResult({
          success: true,
          message: '画像ファイルが正常にアップロードされ、月間デフォルト画像として設定されました',
          details: {
            fileName: selectedFile.name,
            fileType: '画像ファイル',
            imageType: imageInfo?.type === 'header' ? 'ヘッダー画像' : '背景画像',
            month: imageInfo?.month,
            dataLength: imageInfo?.dataLength,
            message: `${imageInfo?.month}月のスケジュール生成時に自動的に使用されます`,
            nextSteps: `次回${imageInfo?.month}月のスケジュールを生成すると、この画像が使用されます。`
          }
        });
      } else {
        // Excel file processing
        const historicalSchedule = await withLoading(() => 
          apiClient.uploadExcelFile(selectedFile)
        );

        setUploadResult({
          success: true,
          message: 'Excelファイルが正常にアップロードされました',
          details: {
            fileName: selectedFile.name,
            fileType: 'Excelファイル',
            eventsCount: historicalSchedule.events.length,
            year: historicalSchedule.year,
            month: historicalSchedule.month,
            categories: Array.from(new Set(historicalSchedule.events.map(e => e.eventCategory)))
          }
        });
      }

      // Clear the selected file after successful upload
      setTimeout(() => {
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 3000);

    } catch (error) {
      console.error('Upload failed:', error);
      setUploadResult({
        success: false,
        message: error instanceof ApiClientError ? error.message : 'アップロード中にエラーが発生しました',
        details: error instanceof ApiClientError ? error.details : undefined
      });
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="file-upload">
      <div className="upload-header">
        <h2>前年度スケジュール・画像アップロード</h2>
        <p>前年度のExcelスケジュールファイルまたは月間デフォルト画像をアップロードできます。</p>
        <p>画像ファイルは現在の月のデフォルト画像として自動設定され、今後のスケジュール生成時に使用されます。</p>
      </div>

      <div className="upload-section">
        <div 
          className={`drop-zone ${selectedFile ? 'has-file' : ''}`}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          
          {selectedFile ? (
            <div className="file-info">
              <div className="file-icon">📄</div>
              <div className="file-details">
                <div className="file-name">{selectedFile.name}</div>
                <div className="file-size">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </div>
              </div>
              <button 
                className="clear-file-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearFile();
                }}
              >
                ✕
              </button>
            </div>
          ) : (
            <div className="drop-zone-content">
              <div className="upload-icon">📁</div>
              <p>Excelファイルまたは画像ファイルをドラッグ&ドロップするか、クリックして選択</p>
              <p className="file-requirements">
                Excel: .xlsx, .xls（最大10MB）<br/>
                画像: .jpg, .png, .gif等（最大5MB）
              </p>
            </div>
          )}
        </div>

        {selectedFile && (
          <div className="upload-actions">
            <button 
              onClick={handleUpload}
              disabled={uploading}
              className="upload-btn"
            >
              {uploading ? 'アップロード中...' : 'アップロード開始'}
            </button>
          </div>
        )}

        {uploading && (
          <div className="upload-progress">
            <div className="loading-spinner">アップロード中...</div>
          </div>
        )}

        {uploadResult && (
          <div className={`upload-result ${uploadResult.success ? 'success' : 'error'}`}>
            <div className="result-message">{uploadResult.message}</div>
            {uploadResult.success && uploadResult.details && (
              <div className="result-details">
                <p><strong>ファイル名:</strong> {uploadResult.details.fileName}</p>
                {uploadResult.details.eventsCount && (
                  <>
                    <p><strong>イベント数:</strong> {uploadResult.details.eventsCount}</p>
                    <p><strong>カテゴリ:</strong> {uploadResult.details.categories.join(', ')}</p>
                  </>
                )}
                {uploadResult.details.imageType && (
                  <>
                    <p><strong>画像タイプ:</strong> {uploadResult.details.imageType}</p>
                    <p><strong>対象月:</strong> {uploadResult.details.month}月</p>
                    {uploadResult.details.dataLength && (
                      <p><strong>データサイズ:</strong> {Math.round(uploadResult.details.dataLength / 1024)}KB</p>
                    )}
                    <p><strong>使用方法:</strong> {uploadResult.details.message}</p>
                    {uploadResult.details.nextSteps && (
                      <p className="next-steps"><strong>次のステップ:</strong> {uploadResult.details.nextSteps}</p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <button 
          onClick={async () => {
            try {
              const currentMonth = new Date().getMonth() + 1;
              const monthlyImages = await apiClient.getMonthlyImages(currentMonth);
              console.log('=== 現在の月間画像状況 ===');
              console.log(`${currentMonth}月のアップロード済み画像:`, monthlyImages);
              
              let message = `${currentMonth}月の画像状況:\n\n`;
              message += `ヘッダー画像: ${monthlyImages.header ? 'アップロード済み ✅' : '未アップロード ❌'}\n`;
              message += `背景画像: ${monthlyImages.background ? 'アップロード済み ✅' : '未アップロード ❌'}\n\n`;
              
              if (monthlyImages.header) {
                message += `ヘッダー画像データ: ${Math.round(monthlyImages.header.length / 1024)}KB\n`;
              }
              if (monthlyImages.background) {
                message += `背景画像データ: ${Math.round(monthlyImages.background.length / 1024)}KB\n`;
              }
              
              message += '\nスケジュール生成時は、アップロード済み画像が優先的に使用されます。';
              
              alert(message);
            } catch (error) {
              console.error('Failed to get monthly images:', error);
              alert('月間画像の取得に失敗しました: ' + (error instanceof Error ? error.message : 'Unknown error'));
            }
          }}
          style={{ 
            marginTop: '16px', 
            padding: '8px 16px', 
            fontSize: '14px', 
            backgroundColor: '#17a2b8', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          現在の月の画像状況を確認
        </button>
      </div>

      <div className="upload-instructions">
        <h3>アップロード要件</h3>
        
        <div className="file-type-section">
          <h4>📊 Excelファイル</h4>
          <ul>
            <li>ファイル形式: .xlsx または .xls</li>
            <li>1列目: 日付（YYYY/MM/DD形式）</li>
            <li>2列目: イベント名</li>
            <li>3列目: イベント種類（オプション）</li>
            <li>ファイルサイズ: 10MB以下</li>
          </ul>
        </div>

        <div className="file-type-section">
          <h4>🖼️ 画像ファイル</h4>
          <ul>
            <li>ファイル形式: .jpg, .png, .gif, .bmp等</li>
            <li>ファイルサイズ: 5MB以下</li>
            <li>ファイル名に「background」「背景」「bg」が含まれる場合は背景画像として設定</li>
            <li>それ以外はヘッダー画像として設定</li>
            <li>現在の月（{new Date().getMonth() + 1}月）のデフォルト画像として自動設定</li>
          </ul>
        </div>
        
        <div className="sample-format">
          <h4>Excelサンプル形式</h4>
          <table>
            <thead>
              <tr>
                <th>日付</th>
                <th>イベント名</th>
                <th>イベント種類</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>2024/01/01</td>
                <td>新年会</td>
                <td>生活</td>
              </tr>
              <tr>
                <td>2024/01/02</td>
                <td>体操教室</td>
                <td>健康と美</td>
              </tr>
              <tr>
                <td>2024/01/03</td>
                <td>手芸クラブ</td>
                <td>趣味活動</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FileUpload;